// src/components/WebsiteUnavailable.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { UtensilsCrossed, Phone } from 'lucide-react';

interface WebsiteUnavailableProps {
  phone?: string;
}

export const WebsiteUnavailable: React.FC<WebsiteUnavailableProps> = ({ phone }) => {
  return (
    <div className="min-h-screen relative flex items-center justify-center px-4">
      {/* Background Pattern Layer - Fade In */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="absolute inset-0 overflow-hidden"
        style={{ backgroundColor: '#f5f5f5' }}
      >
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="restaurant-icons" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
              {/* Fork */}
              <g opacity="0.04">
                <line x1="20" y1="10" x2="20" y2="40" stroke="#EE4423" strokeWidth="2"/>
                <line x1="15" y1="10" x2="15" y2="25" stroke="#EE4423" strokeWidth="2"/>
                <line x1="25" y1="10" x2="25" y2="25" stroke="#EE4423" strokeWidth="2"/>
              </g>
              
              {/* Knife */}
              <g opacity="0.04">
                <line x1="70" y1="10" x2="70" y2="40" stroke="#EE4423" strokeWidth="2"/>
                <path d="M65,10 L75,10 L70,20 Z" fill="#EE4423"/>
              </g>
              
              {/* Plate */}
              <g opacity="0.04">
                <circle cx="45" cy="75" r="12" stroke="#EE4423" strokeWidth="2" fill="none"/>
                <circle cx="45" cy="75" r="8" stroke="#EE4423" strokeWidth="1" fill="none"/>
              </g>
              
              {/* Spoon */}
              <g opacity="0.04">
                <ellipse cx="95" cy="75" rx="5" ry="7" fill="#EE4423"/>
                <line x1="95" y1="82" x2="95" y2="105" stroke="#EE4423" strokeWidth="2"/>
              </g>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#restaurant-icons)"/>
        </svg>
      </motion.div>

      {/* Content (on top of pattern) */}
      <div className="max-w-md w-full text-center relative z-10">
        {/* Restaurant Icon - Fade + Scale */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, type: "spring", stiffness: 200 }}
          className="flex justify-center mb-6"
        >
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
            <UtensilsCrossed className="w-12 h-12 text-orange-600" />
          </div>
        </motion.div>

        {/* Title - Fade + Slide Up */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-3xl font-bold text-gray-900 mb-4"
        >
          Website Currently Unavailable
        </motion.h1>

        {/* Message - Fade + Slide Up */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-lg text-gray-600 mb-8"
        >
          This restaurant's website is temporarily unavailable. Please contact the
          restaurant directly for reservations, menu details, and other inquiries.
        </motion.p>

        {/* Contact Button - Fade + Slide Up */}
        {phone && (
          <motion.a
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            href={`tel:${phone}`}
            className="inline-flex items-center gap-2 px-6 py-3 text-white font-semibold rounded-lg transition-colors"
            style={{ backgroundColor: '#EE4423' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#D63D1F'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EE4423'}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Phone className="w-5 h-5" />
            Contact Restaurant
          </motion.a>
        )}
      </div>
    </div>
  );
};

export default WebsiteUnavailable;
